import {
  AppBar, Box, CssBaseline, IconButton, Toolbar, Typography, Drawer,
  List, ListItemButton, ListItemIcon, ListItemText, Divider,
  useTheme, useMediaQuery, Avatar, Badge, Tooltip, Paper,
  ClickAwayListener, Popper, Fade, CircularProgress,
  Dialog, DialogContent, TextField, Button,
  InputAdornment, Stack,
} from '@mui/material';
import CustomTooltip from './CustomTooltip';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import BookIcon from '@mui/icons-material/Book';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContactsIcon from '@mui/icons-material/Contacts';
import ForumIcon from '@mui/icons-material/Forum';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CampaignIcon from '@mui/icons-material/Campaign';

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNotifications } from '../context/NotificationContext';
import { verificationAPI, supportAPI, notificationsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { getNotificationVisual } from '../utils/customerNotifications';
import logo from '../assets/logo3.png';
import logo1 from '../assets/logo1.png';

const normalizeProfile = (raw) => {
  const p = raw && typeof raw === 'object' ? raw : {};
  const firstName = p.firstName ?? p.first_name ?? '';
  const lastName = p.lastName ?? p.last_name ?? '';
  const businessName =
    p.businessName ?? p.company_BusinessName ?? p.companyBusinessName ?? '';
  const displayName =
    p.displayName ||
    p.company_BusinessName ||
    p.name ||
    p.fullName ||
    p.userName ||
    businessName ||
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    '';
  const email = p.email || p.userEmail || '';
  const role =
    p.role ||
    p.userRole ||
    p.accountType ||
    p.userType ||
    p.type ||
    '';
  const userType = p.userType || p.accountType || p.type || p.role || '';
  return { displayName, email, role, firstName, lastName, businessName, userType };
};

const resolveProfileFromApiRoot = (root) => {
  if (root.profile && typeof root.profile === 'object') {
    return root.profile;
  }
  return root;
};

const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${c.codePointAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('decodeToken failed:', error);
    return null;
  }
};


/** Company vs freelancer: used to choose display-name form fields */
const isCompanyProfile = (profile, authUser) => {
  const p = profile || {};
  const u = authUser || {};
  const blob = [p.userType, p.accountType, p.type, p.role, u?.userType, u?.accountType, u?.type, u?.role]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/(company|business|corp)/.test(blob)) return true;
  if (/(freelancer|individual)/.test(blob)) return false;
  if (p.isCompany === true || u?.isCompany === true) return true;
  if (p.isCompany === false || u?.isCompany === false) return false;
  const bn = p.businessName ?? p.company_BusinessName ?? '';
  const fn = p.firstName ?? p.first_name ?? '';
  const ln = p.lastName ?? p.last_name ?? '';
  if (bn && !fn && !ln) return true;
  return false;
};

const initialsFromName = (name, authUser) => {
  let displayName = name;
  if (!displayName && authUser) {
    const normalized = normalizeProfile(authUser);
    displayName = normalized.displayName || normalized.email;
  }
  if (!displayName || typeof displayName !== 'string') return 'GS';
  const cleanName = displayName.trim();
  if (!cleanName) return 'GS';
  if (cleanName.includes('@')) {
    const localPart = cleanName.split('@')[0].replace(/[^a-zA-Z0-9]/g, ' ').trim();
    const parts = localPart.split(/\s+/).filter(Boolean);
    if (!parts.length) return 'GS';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts.at(-1)[0]).toUpperCase();
  }
  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (!parts.length) return 'GS';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts.at(-1)[0]).toUpperCase();
};

const validatePassword = (password) => {
  if (!password) return 'required';
  if (password.length > 12) return 'tooLong';
  if (password.length < 8) return 'tooShort';
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&#]/.test(password);
  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) return 'weak';
  return null;
};

const drawerWidth = 240;
const closedWidth = 64;

const GS_COLORS = {
  blue: '#FF8C42',
  blueLight: '#FFF5EF',
  orange: '#FF8C42',
  orangeLight: '#FFF5EF',
  white: '#ffffff',
  textPrimary: '#1E3A45',
  textMuted: '#6B7280',
  sidebarBorder: '#E5E7EB',
  activeItemBg: '#FFF5EF',
  activeItemText: '#E06526',
  activeItemBorder: '#FF8C42',
  hoverBg: '#F3F4F6',
  hoverText: '#1E3A45',
  notifUnread: '#FFF5EF',
  notifHover: '#F8FAFC',
  notifBorder: '#E5E7EB',
  /** Change display name dialog — header + field focus (reference UI) */
  dialogHeaderPeach: '#FFF5EF',
  dialogIconPeach: '#FFD6BA',
  dialogFieldFocusBlue: '#FF8C42',
};

const PROFILE_DIALOG_RADIUS = '16px';

const profileDialogFieldSx = {
  '& .MuiInputLabel-root': {
    color: GS_COLORS.textPrimary,
    fontWeight: 500,
    '&.Mui-focused': { color: GS_COLORS.orange },
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: GS_COLORS.textPrimary,
    '& fieldset': { borderColor: 'rgba(30, 58, 69, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(30, 58, 69, 0.35)' },
    '&.Mui-focused fieldset': { borderColor: GS_COLORS.orange, borderWidth: 2 },
  },
};

/** Display name dialog fields — blue label + outline on focus (screenshot style) */
const profileDialogDisplayNameFieldSx = {
  '& .MuiInputLabel-root': {
    color: GS_COLORS.textPrimary,
    fontWeight: 500,
    '&.Mui-focused': { color: GS_COLORS.dialogFieldFocusBlue },
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: GS_COLORS.textPrimary,
    '& fieldset': { borderColor: 'rgba(30, 58, 69, 0.22)' },
    '&:hover fieldset': { borderColor: 'rgba(30, 58, 69, 0.38)' },
    '&.Mui-focused fieldset': { borderColor: GS_COLORS.dialogFieldFocusBlue, borderWidth: 2 },
  },
};

const profileDialogPaperProps = {
  elevation: 0,
  sx: {
    borderRadius: PROFILE_DIALOG_RADIUS,
    border: `1px solid ${GS_COLORS.notifBorder}`,
    maxWidth: 460,
    width: '100%',
    /** Clip children so bottom matches top corner radius */
    overflow: 'hidden',
    boxShadow: '0 20px 45px -12px rgba(15, 23, 42, 0.12)',
  },
};

const getNavItems = (t, chatUnreadCount) => [
  { label: t('layout.nav.dashboard'), path: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
  { label: t('layout.nav.contacts'), path: '/contacts', icon: <ContactsIcon fontSize="small" /> },
  { label: t('layout.nav.bookings'), path: '/bookings', icon: <EventIcon fontSize="small" /> },
  {
    label: t('layout.nav.chat'),
    path: '/chat',
    unreadCount: chatUnreadCount,
    icon: (
      <Badge
        badgeContent={chatUnreadCount > 99 ? '99+' : chatUnreadCount}
        invisible={chatUnreadCount < 1}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#EF4444',
            color: '#fff',
            fontSize: '9px',
            height: '14px',
            minWidth: '14px',
            fontWeight: 700,
            top: 2,
            right: -2,
            padding: '0 4px',
            boxShadow: chatUnreadCount > 10 ? '0 0 6px 2px rgba(239, 68, 68, 0.5)' : 'none',
            animation: chatUnreadCount > 10 ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)',
              },
              '70%': {
                boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)',
              },
            },
          },
        }}
      >
        <ForumIcon fontSize="small" />
      </Badge>
    ),
  },
  { label: t('layout.nav.slots'), path: '/slots', icon: <AccessTimeIcon fontSize="small" /> },
  { label: t('layout.nav.batchJobs'), path: '/batch-jobs', icon: <ScheduleIcon fontSize="small" /> },
  { label: t('layout.nav.promotions'), path: '/promotions', icon: <CampaignIcon fontSize="small" /> },
  { label: t('layout.nav.customPush') || 'Push Notifications', path: '/custom-push-notifications', icon: <NotificationsActiveIcon fontSize="small" /> },
  { label: t('layout.nav.bookingTemplates'), path: '/booking-templates', icon: <BookIcon fontSize="small" /> },

  { label: t('layout.nav.bookingUsers'), path: '/user-booking-blocks', icon: <GroupIcon fontSize="small" /> },
  { label: t('layout.nav.enquiry'), path: '/enquiry', icon: <MailOutlineIcon fontSize="small" /> },
  { label: t('layout.nav.faq'), path: '/faq', icon: <HelpOutlineIcon fontSize="small" /> },
];

function getNewPasswordHelperText(attemptedPasswordSave, newPassword, translate) {
  const showValidation = attemptedPasswordSave || newPassword.length > 0;
  if (!showValidation) return '';
  const passVal = validatePassword(newPassword);
  if (passVal === 'required') {
    return attemptedPasswordSave ? translate('pages.bookingTemplates.validation.required') : '';
  }
  if (passVal === 'tooShort') return translate('common.validation.passwordTooShort');
  if (passVal === 'tooLong') return translate('common.validation.passwordTooLong');
  if (passVal === 'weak') return translate('common.validation.passwordWeak');
  return '';
}

function isPasswordMismatch(confirmPassword, newPassword) {
  return Boolean(confirmPassword && newPassword && confirmPassword !== newPassword);
}

function getConfirmPasswordHelperText(confirmPassword, newPassword, translate) {
  if (isPasswordMismatch(confirmPassword, newPassword)) {
    return translate('layout.toast.passwordMismatch');
  }
  return '';
}

function getConfirmPasswordFieldSx(confirmPassword, newPassword) {
  if (!isPasswordMismatch(confirmPassword, newPassword)) {
    return profileDialogFieldSx;
  }
  return {
    ...profileDialogFieldSx,
    '& .MuiOutlinedInput-root': {
      ...profileDialogFieldSx['& .MuiOutlinedInput-root'],
      '& fieldset': { borderColor: '#DC2626' },
      '&:hover fieldset': { borderColor: '#DC2626' },
    },
  };
}

function getPasswordVisibilityIcon(showPassword) {
  if (showPassword) return <VisibilityOff fontSize="small" />;
  return <Visibility fontSize="small" />;
}

function ProfileHeaderContent({ profileLoading, profileError, profileInfo }) {
  if (profileLoading) {
    return <CircularProgress size={24} sx={{ color: GS_COLORS.orange }} />;
  }
  if (profileError) {
    return <Typography sx={{ fontSize: 13, color: '#DC2626' }}>{profileError}</Typography>;
  }
  return (
    <>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: GS_COLORS.textPrimary, lineHeight: 1.3 }}>
        {profileInfo.displayName || '—'}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: GS_COLORS.textMuted,
          mt: 0.5,
          wordBreak: 'break-word',
          px: 0.5,
        }}
      >
        {profileInfo.email || '—'}
      </Typography>
    </>
  );
}

function NotificationListContent({
  notifLoading,
  notifError,
  notifPreviewItems,
  translate,
  formatNotifDate,
  onNotifRowClick,
}) {
  if (notifLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: GS_COLORS.orange }} />
      </Box>
    );
  }
  if (notifError) {
    return (
      <Typography sx={{ px: 2.5, py: 3, fontSize: 13, color: GS_COLORS.textMuted }}>
        {notifError}
      </Typography>
    );
  }
  if (notifPreviewItems.length === 0) {
    return (
      <Typography sx={{ px: 2.5, py: 3, fontSize: 13, color: GS_COLORS.textMuted }}>
        {translate('layout.notifications.empty')}
      </Typography>
    );
  }

  return notifPreviewItems.map((notif, idx) => {
    const vis = getNotificationVisual(notif.category, notif.relatedEntityType);
    const IconComp = vis.Icon;
    return (
      <Box
        key={notif.id}
        onClick={() => onNotifRowClick(notif)}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
          px: 2.5, py: 1.8, cursor: 'pointer',
          backgroundColor: notif.isRead ? 'transparent' : GS_COLORS.notifUnread,
          borderBottom: idx < notifPreviewItems.length - 1
            ? `1px solid ${GS_COLORS.notifBorder}` : 'none',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: GS_COLORS.notifHover },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            flexShrink: 0,
            mt: 0.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: vis.bg,
            color: vis.color,
          }}
        >
          <IconComp sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: GS_COLORS.textPrimary, mb: 0.4 }}>
            {notif.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              color: GS_COLORS.textMuted,
              mb: 0.6,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {notif.body}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeFilledIcon sx={{ fontSize: 13, color: GS_COLORS.textMuted }} />
            <Typography sx={{ fontSize: 11.5, color: GS_COLORS.textMuted }}>
              {formatNotifDate(notif.createdAtUtc)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  });
}

function NameDialogFields({
  companyUser,
  businessNameInput,
  setBusinessNameInput,
  firstNameInput,
  setFirstNameInput,
  lastNameInput,
  setLastNameInput,
  savingName,
  translate,
}) {
  if (companyUser) {
    return (
      <TextField
        autoFocus
        label={translate('layout.dialog.businessName')}
        fullWidth
        variant="outlined"
        value={businessNameInput}
        onChange={(e) => setBusinessNameInput(e.target.value)}
        disabled={savingName}
        sx={profileDialogDisplayNameFieldSx}
      />
    );
  }

  return (
    <>
      <TextField
        autoFocus
        label={translate('layout.dialog.firstName')}
        fullWidth
        variant="outlined"
        value={firstNameInput}
        onChange={(e) => setFirstNameInput(e.target.value)}
        disabled={savingName}
        sx={profileDialogDisplayNameFieldSx}
      />
      <TextField
        label={translate('layout.dialog.lastName')}
        fullWidth
        variant="outlined"
        value={lastNameInput}
        onChange={(e) => setLastNameInput(e.target.value)}
        disabled={savingName}
        sx={profileDialogDisplayNameFieldSx}
      />
    </>
  );
}

function LanguageToggleButtons({ locale, setLocale, translate }) {
  const getLocaleButtonSx = (code) => ({
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    ...(locale === code
      ? { bgcolor: GS_COLORS.orange, '&:hover': { bgcolor: '#E06526' } }
      : { borderColor: GS_COLORS.notifBorder, color: GS_COLORS.textPrimary }),
  });

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button fullWidth size="small" variant={locale === 'en' ? 'contained' : 'outlined'} onClick={() => setLocale('en')} sx={getLocaleButtonSx('en')}>
        {translate('layout.profile.languageEn')}
      </Button>
      <Button fullWidth size="small" variant={locale === 'de' ? 'contained' : 'outlined'} onClick={() => setLocale('de')} sx={getLocaleButtonSx('de')}>
        {translate('layout.profile.languageDe')}
      </Button>
    </Box>
  );
}

function resolveDrawerWidth(isDesktop, open) {
  if (!isDesktop) return drawerWidth;
  if (open) return drawerWidth;
  return closedWidth;
}

const buildNavItemSx = (path, pathname, open) => {
  const active = pathname.startsWith(path);
  return {
    justifyContent: open ? 'initial' : 'center',
    px: 2.5, mx: 1, borderRadius: '8px', mb: 0.5,
    borderLeft: active ? `3px solid ${GS_COLORS.activeItemBorder}` : '3px solid transparent',
    backgroundColor: active ? GS_COLORS.activeItemBg : 'transparent',
    color: active ? GS_COLORS.activeItemText : GS_COLORS.textMuted,
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: active ? GS_COLORS.activeItemBg : GS_COLORS.hoverBg,
      color: active ? GS_COLORS.activeItemText : GS_COLORS.hoverText,
      '& .MuiListItemIcon-root': { color: active ? GS_COLORS.activeItemText : GS_COLORS.hoverText },
    },
    '& .MuiListItemIcon-root': {
      color: active ? GS_COLORS.activeItemText : GS_COLORS.textMuted,
      minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', transition: 'color 0.15s ease',
    },
  };
};

function LayoutSidebar({
  isDesktop,
  open,
  setOpen,
  navItems,
  pathname,
  navigate,
  logout,
  setHelpCenterOpen,
  translate,
}) {
  const showNavLabels = open || !isDesktop;
  const drawerWidthValue = resolveDrawerWidth(isDesktop, open);

  const handleNavClick = (path, isAction) => {
    if (isAction) {
      if (path === 'help-center') setHelpCenterOpen(true);
    } else {
      navigate(path);
    }
    if (!isDesktop) setOpen(false);
  };

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      anchor="left"
      open={open}
      onClose={() => setOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidthValue,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidthValue,
          overflowX: 'hidden',
          overflowY: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: GS_COLORS.white,
          borderRight: `1px solid ${GS_COLORS.sidebarBorder}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Toolbar sx={{ flexShrink: 0 }} />
      <Divider sx={{ borderColor: GS_COLORS.sidebarBorder, flexShrink: 0 }} />

      <Box
        sx={{
          flex: 1,
          overflowY: open || !isDesktop ? 'auto' : 'hidden',
          overflowX: 'hidden',
          pt: 1.5,
          /* ── Modern custom scrollbar styling ── */
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            marginTop: '6px',
            marginBottom: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(156, 163, 175, 0.35)',
            borderRadius: '10px',
            transition: 'background-color 0.2s ease',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: GS_COLORS.orange,
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.35) transparent',
        }}
      >
        <List disablePadding>
          {navItems.map(({ label, path, icon, isAction, unreadCount }) => {
            const isChatWithManyUnread = path === '/chat' && unreadCount > 10;
            const tooltipTitle = isChatWithManyUnread
              ? translate('layout.nav.chatUnread', { count: unreadCount })
              : label;
            return (
              <CustomTooltip
                key={path}
                title={tooltipTitle}
                placement="right"
                disableHoverListener={!isDesktop || (open && !isChatWithManyUnread)}
              >
                <ListItemButton
                  sx={buildNavItemSx(path, pathname, open)}
                  onClick={() => handleNavClick(path, isAction)}
                >
                  <ListItemIcon>{icon}</ListItemIcon>
                  {showNavLabels && (
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: pathname.startsWith(path) ? 500 : 400,
                      }}
                    />
                  )}
                </ListItemButton>
              </CustomTooltip>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ borderColor: GS_COLORS.sidebarBorder, flexShrink: 0 }} />

      <List sx={{ flexShrink: 0 }}>
        <CustomTooltip title={translate('layout.profile.logout')} placement="right" disableHoverListener={!isDesktop || open}>
          <ListItemButton
            sx={{
              justifyContent: showNavLabels ? 'initial' : 'center',
              px: 2.5, mx: 1, borderRadius: '8px', mb: 0.5,
              borderLeft: '3px solid transparent',
              color: GS_COLORS.textMuted,
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: '#FEF2F2', color: '#c54b4b',
                '& .MuiListItemIcon-root': { color: '#DC2626' },
              },
              '& .MuiListItemIcon-root': {
                color: GS_COLORS.textMuted, minWidth: 0,
                mr: showNavLabels ? 3 : 'auto', justifyContent: 'center', transition: 'color 0.15s ease',
              },
            }}
            onClick={() => {
              logout();
              navigate('/login');
              if (!isDesktop) setOpen(false);
            }}
          >
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            {showNavLabels && <ListItemText primary={translate('layout.profile.logout')} primaryTypographyProps={{ fontSize: 14 }} />}
          </ListItemButton>
        </CustomTooltip>
      </List>
    </Drawer>
  );
}

const Layout = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [open, setOpen] = useState(isDesktop);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifAnchorRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileAnchorRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, token } = useAuth();
  const [profileInfo, setProfileInfo] = useState(() => normalizeProfile(user));
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [businessNameInput, setBusinessNameInput] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [submittingHelp, setSubmittingHelp] = useState(false);
  const [notifMarkingAll, setNotifMarkingAll] = useState(false);
  const {
    previewItems: notifPreviewItems,
    unreadCount: notifUnreadApi,
    chatUnreadCount,
    loading: notifLoading,
    error: notifError,
    refresh: refreshNotifications,
  } = useNotifications();

  const { t, locale, setLocale } = useLanguage();
  const isNotificationsPage = location.pathname.endsWith('/notifications');

  const navItems = useMemo(() => getNavItems(t, chatUnreadCount), [t, chatUnreadCount]);

  useEffect(() => { setOpen(isDesktop); }, [isDesktop]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileInfo((prev) => {
        const norm = normalizeProfile(user);
        return {
          ...norm,
          ...prev,
          displayName: prev.displayName || norm.displayName,
          email: prev.email || norm.email,
        };
      });
    }
  }, [user]);

  useEffect(() => {
    if (notifOpen) refreshNotifications(false);
  }, [notifOpen, refreshNotifications]);

  useEffect(() => {
    if (isNotificationsPage) refreshNotifications(true);
  }, [isNotificationsPage, refreshNotifications]);

  useEffect(() => {
    if (isNotificationsPage) setNotifOpen(false);
  }, [isNotificationsPage]);

  const formatNotifDate = useCallback(
    (iso) => {
      if (!iso) return '';
      try {
        const loc = locale === 'de' ? 'de-DE' : 'en-US';
        return new Date(iso).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
      } catch {
        return String(iso);
      }
    },
    [locale]
  );

  const handleNotifRowClick = async (item) => {
    if (item?.deepLink) {
      setNotifOpen(false);
      navigate(item.deepLink);
      return;
    }
    if (!item?.id || item.isRead) return;
    try {
      await notificationsAPI.markRead(item.id);
      await refreshNotifications(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.errorMessage || t('layout.toast.error'));
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    if (notifMarkingAll || notifUnreadApi < 1) return;
    setNotifMarkingAll(true);
    try {
      await notificationsAPI.markAllRead();
      await refreshNotifications(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.errorMessage || t('layout.toast.error'));
    } finally {
      setNotifMarkingAll(false);
    }
  };

  const openNotificationsViewAll = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setNotifOpen(false);
    navigate('/notifications');
    refreshNotifications(true);
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const res = await verificationAPI.getProfile();
      const root = res?.data?.data ?? res?.data ?? {};
      setProfileInfo(normalizeProfile(resolveProfileFromApiRoot(root)));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to load profile';
      setProfileError(msg);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileToggle = () => {
    if (profileOpen) {
      setProfileOpen(false);
      return;
    }
    setProfileOpen(true);
    fetchProfile();
  };

  const openNameDialog = () => {
    setFirstNameInput(profileInfo.firstName || '');
    setLastNameInput(profileInfo.lastName || '');
    setBusinessNameInput(
      profileInfo.businessName || (isCompanyProfile(profileInfo, user) ? profileInfo.displayName : '') || ''
    );
    setNameDialogOpen(true);
    setProfileOpen(false);
  };

  const openPasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowConfirmPassword(false);
    setAttemptedPasswordSave(false);
    setPasswordDialogOpen(true);
    setProfileOpen(false);
  };

  const [attemptedPasswordSave, setAttemptedPasswordSave] = useState(false);

  const companyUser = isCompanyProfile(profileInfo, user);

  const nameFormValid = companyUser
    ? Boolean(businessNameInput.trim())
    : Boolean(firstNameInput.trim() || lastNameInput.trim());

  const handleSaveName = async () => {
    if (!nameFormValid) return;
    setSavingName(true);
    try {
      await verificationAPI.updateDisplayName({
        firstName: companyUser ? '' : firstNameInput.trim(),
        lastName: companyUser ? '' : lastNameInput.trim(),
        businessName: companyUser ? businessNameInput.trim() : '',
      });
      toast.success(t('layout.toast.nameUpdated'));
      setNameDialogOpen(false);
      await fetchProfile();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.response?.data?.error?.message || t('layout.toast.error')
      );
    } finally {
      setSavingName(false);
    }
  };

  const handleSavePassword = async () => {
    setAttemptedPasswordSave(true);
    const passVal = validatePassword(newPassword);
    if (!currentPassword || passVal !== null) return;
    if (newPassword !== confirmPassword) {
      toast.error(t('layout.toast.passwordMismatch'));
      return;
    }
    setSavingPassword(true);
    try {
      await verificationAPI.changePassword({
        oldPassword: currentPassword,
        newPassword,
        confirmPassword,
      });
      toast.success(t('layout.toast.passwordUpdated'));
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setAttemptedPasswordSave(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.response?.data?.error?.message || t('layout.toast.error')
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveHelp = async () => {
    if (!helpMessage.trim()) return;
    setSubmittingHelp(true);
    try {
      const decoded = decodeToken(token);
      const cityIdFromToken = decoded?.cityId || decoded?.CityId || 0;

      await supportAPI.submitSupport({
        name: profileInfo.displayName || user?.displayName || 'User',
        emailId: profileInfo.email || user?.email || '',
        message: helpMessage.trim(),
        cityId: cityIdFromToken,
      });

      toast.success(t('layout.toast.helpCenterSuccess'));
      setHelpCenterOpen(false);
      setHelpMessage('');
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.response?.data?.error?.message || t('layout.toast.error')
      );
    } finally {
      setSubmittingHelp(false);
    }
  };

  const showNotificationUi = !isNotificationsPage;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* ── Header ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: GS_COLORS.white,
          borderBottom: `3px solid ${GS_COLORS.blue}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>

          {/* Left */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ mr: 1, color: GS_COLORS.blue, '&:hover': { backgroundColor: GS_COLORS.blueLight } }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={logo1}
              alt="Logo"
              sx={{
                height: 35,
                display: 'block',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* Right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

            {/* Notification bell hidden on full notifications page */}
            {showNotificationUi ? (
              <Tooltip title={t('layout.notifications.title')}>
                <IconButton
                  ref={notifAnchorRef}
                  onClick={() => setNotifOpen((p) => !p)}
                  sx={{
                    color: notifOpen ? GS_COLORS.blue : GS_COLORS.textMuted,
                    backgroundColor: notifOpen ? GS_COLORS.blueLight : 'transparent',
                    width: 38,
                    height: 38,
                    '&:hover': { backgroundColor: GS_COLORS.blueLight, color: GS_COLORS.blue },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Badge
                    badgeContent={notifUnreadApi > 99 ? '99+' : notifUnreadApi}
                    invisible={notifUnreadApi < 1}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#EF4444',
                        color: '#fff',
                        fontSize: '10px',
                        height: '16px',
                        minWidth: '16px',
                        fontWeight: 700,
                        top: 1,
                        right: 1,
                      },
                    }}
                  >
                    <NotificationsNoneIcon sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : null}

            {/* Profile Avatar */}
            <Tooltip title={t('layout.profile.tooltip')}>
              <IconButton
                ref={profileAnchorRef}
                onClick={handleProfileToggle}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 34, height: 34,
                    backgroundColor: GS_COLORS.blue,
                    color: GS_COLORS.white,
                    fontSize: '13px', fontWeight: 700,
                    border: `2px solid ${GS_COLORS.blueLight}`,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: `0 0 0 3px ${GS_COLORS.blueLight}` },
                  }}
                >
                  {initialsFromName(profileInfo.displayName, user)}
                </Avatar>
              </IconButton>
            </Tooltip>

          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Notification Dropdown ── */}
      <Popper
        open={showNotificationUi && notifOpen}
        anchorEl={notifAnchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <div>
              <ClickAwayListener onClickAway={() => setNotifOpen(false)}>
                <Paper
                  elevation={8}
                  sx={{
                    width: 360,
                    mt: 1,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: GS_COLORS.white,
                    border: `1px solid ${GS_COLORS.notifBorder}`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  }}
                >
                  {/* Dropdown Header */}
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2.5, py: 2,
                      borderBottom: `1px solid ${GS_COLORS.notifBorder}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: GS_COLORS.textPrimary }}>
                      {t('layout.notifications.title')}
                    </Typography>
                    <Box
                      role="button"
                      onClick={handleMarkAllNotificationsRead}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: 13,
                        color: GS_COLORS.orange,
                        fontWeight: 500,
                        cursor: notifMarkingAll || notifUnreadApi < 1 ? 'default' : 'pointer',
                        opacity: notifMarkingAll || notifUnreadApi < 1 ? 0.45 : 1,
                        '&:hover': {
                          textDecoration: notifMarkingAll || notifUnreadApi < 1 ? 'none' : 'underline',
                        },
                      }}
                    >
                      {notifMarkingAll && (
                        <CircularProgress size={14} thickness={5} sx={{ color: GS_COLORS.orange }} />
                      )}
                      <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'inherit' }}>
                        {notifMarkingAll ? t('layout.notifications.marking') : t('layout.notifications.markAllRead')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Items */}
                  <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                    <NotificationListContent
                      notifLoading={notifLoading}
                      notifError={notifError}
                      notifPreviewItems={notifPreviewItems}
                      translate={t}
                      formatNotifDate={formatNotifDate}
                      onNotifRowClick={handleNotifRowClick}
                    />
                  </Box>

                  {/* Footer */}
                  <Box
                    onClick={(e) => openNotificationsViewAll(e)}
                    sx={{
                      px: 2.5, py: 1.5,
                      borderTop: `1px solid ${GS_COLORS.notifBorder}`,
                      textAlign: 'center', cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: GS_COLORS.notifHover },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: GS_COLORS.orange, fontWeight: 600 }}>
                      {t('layout.notifications.viewAll')}
                    </Typography>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>

      {/* ── Profile dropdown ── */}
      <Popper
        open={profileOpen}
        anchorEl={profileAnchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 1400 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <div>
              <ClickAwayListener onClickAway={() => setProfileOpen(false)}>
                <Paper
                  elevation={8}
                  sx={{
                    width: 320,
                    mt: 1,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: GS_COLORS.white,
                    border: `1px solid ${GS_COLORS.notifBorder}`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      pt: 2.5,
                      pb: 2,
                      textAlign: 'center',
                      borderBottom: `1px solid ${GS_COLORS.notifBorder}`,
                      background: 'linear-gradient(180deg, #fff 0%, #fafbfc 100%)',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        mx: 'auto',
                        mb: 1.5,
                        backgroundColor: GS_COLORS.orange,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        border: `3px solid ${GS_COLORS.orangeLight}`,
                      }}
                    >
                      {initialsFromName(profileInfo.displayName, user)}
                    </Avatar>
                    <ProfileHeaderContent
                      profileLoading={profileLoading}
                      profileError={profileError}
                      profileInfo={profileInfo}
                    />
                  </Box>

                  <List dense disablePadding sx={{ py: 1 }}>
                    <ListItemButton
                      onClick={openNameDialog}
                      sx={{ py: 1.25, px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PersonOutlineIcon sx={{ color: GS_COLORS.orange, fontSize: 22 }} />
                      </ListItemIcon>
                      <ListItemText primary={t('layout.profile.changeName')} primaryTypographyProps={{ fontSize: 14 }} />
                    </ListItemButton>
                    <ListItemButton
                      onClick={openPasswordDialog}
                      sx={{ py: 1.25, px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <LockOutlinedIcon sx={{ color: GS_COLORS.orange, fontSize: 22 }} />
                      </ListItemIcon>
                      <ListItemText primary={t('layout.profile.changePassword')} primaryTypographyProps={{ fontSize: 14 }} />
                    </ListItemButton>
                    <ListItemButton
                      onClick={() => {
                        setProfileOpen(false);
                        setHelpCenterOpen(true);
                      }}
                      sx={{ py: 1.25, px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <HelpOutlineIcon sx={{ color: GS_COLORS.orange, fontSize: 22 }} />
                      </ListItemIcon>
                      <ListItemText primary={t('layout.nav.helpCenter')} primaryTypographyProps={{ fontSize: 14 }} />
                    </ListItemButton>

                  </List>

                  <Divider />

                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LanguageIcon sx={{ fontSize: 20, color: GS_COLORS.textMuted }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: GS_COLORS.textPrimary }}>
                        {t('layout.profile.language')}
                      </Typography>
                    </Box>
                    <LanguageToggleButtons locale={locale} setLocale={setLocale} translate={t} />
                  </Box>

                  <Divider />

                  <ListItemButton
                    onClick={() => {
                      setProfileOpen(false);
                      logout();
                      navigate('/login');
                    }}
                    sx={{ py: 1.25, px: 2, color: '#c54b4b', '&:hover': { bgcolor: '#FEF2F2' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LogoutIcon sx={{ color: '#DC2626', fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText primary={t('layout.profile.logout')} primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
                  </ListItemButton>
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>

      <Dialog
        open={nameDialogOpen}
        onClose={() => !savingName && setNameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        scroll="body"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px -12px rgba(15, 23, 42, 0.18)',
            border: `1px solid ${GS_COLORS.notifBorder}`,
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        {/* ── Gradient header banner ── */}
        <Box
          sx={{
            background: `linear-gradient(135deg, #FFF0E6 0%, #FFE4CC 100%)`,
            pt: 4,
            pb: 3.5,
            px: 3,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Close icon */}
          <IconButton
            size="small"
            onClick={() => !savingName && setNameDialogOpen(false)}
            disabled={savingName}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: GS_COLORS.textMuted,
              '&:hover': { bgcolor: 'rgba(30,58,69,0.08)', color: GS_COLORS.textPrimary },
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 300 }}>✕</span>
          </IconButton>

          {/* Avatar */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: GS_COLORS.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 24px rgba(255, 140, 66, 0.35)',
            }}
          >
            <PersonOutlineIcon sx={{ color: '#fff', fontSize: 36 }} />
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.2rem',
              color: GS_COLORS.textPrimary,
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            {t('layout.dialog.changeNameTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: GS_COLORS.textMuted, lineHeight: 1.5, px: 1 }}
          >
            {t('layout.dialog.changeNameSubtitle')}
          </Typography>
        </Box>

        {/* ── Fields ── */}
        <DialogContent sx={{ pt: 3, pb: 1, px: 3, bgcolor: '#fff', overflow: 'visible' }}>
          <Stack spacing={2}>
            <NameDialogFields
              companyUser={companyUser}
              businessNameInput={businessNameInput}
              setBusinessNameInput={setBusinessNameInput}
              firstNameInput={firstNameInput}
              setFirstNameInput={setFirstNameInput}
              lastNameInput={lastNameInput}
              setLastNameInput={setLastNameInput}
              savingName={savingName}
              translate={t}
            />
          </Stack>
        </DialogContent>

        {/* ── Actions ── */}
        <Box sx={{ px: 3, pt: 2, pb: 3, bgcolor: '#fff' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveName}
            disabled={savingName || !nameFormValid}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              borderRadius: '12px',
              py: 1.4,
              bgcolor: GS_COLORS.orange,
              boxShadow: '0 6px 16px rgba(255, 140, 66, 0.38)',
              '&:hover': { bgcolor: '#E06526', boxShadow: '0 8px 20px rgba(255, 140, 66, 0.45)' },
              '&.Mui-disabled': { bgcolor: 'rgba(232,113,26,0.38)', color: '#fff' },
            }}
          >
            {savingName ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('layout.dialog.save')}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => setNameDialogOpen(false)}
            disabled={savingName}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              mt: 1,
              color: GS_COLORS.textMuted,
              '&:hover': { bgcolor: 'rgba(30, 58, 69, 0.05)', color: GS_COLORS.textPrimary },
            }}
          >
            {t('layout.dialog.cancel')}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={passwordDialogOpen}
        onClose={() => !savingPassword && setPasswordDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        scroll="body"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px -12px rgba(15, 23, 42, 0.18)',
            border: `1px solid ${GS_COLORS.notifBorder}`,
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        {/* ── Gradient header banner ── */}
        <Box
          sx={{
            background: `linear-gradient(135deg, #FFF0E6 0%, #FFE4CC 100%)`,
            pt: 4,
            pb: 3.5,
            px: 3,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Close icon */}
          <IconButton
            size="small"
            onClick={() => !savingPassword && setPasswordDialogOpen(false)}
            disabled={savingPassword}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: GS_COLORS.textMuted,
              '&:hover': { bgcolor: 'rgba(30,58,69,0.08)', color: GS_COLORS.textPrimary },
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 300 }}>✕</span>
          </IconButton>

          {/* Avatar */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: GS_COLORS.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 24px rgba(255, 140, 66, 0.35)',
            }}
          >
            <LockOutlinedIcon sx={{ color: '#fff', fontSize: 34 }} />
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.2rem',
              color: GS_COLORS.textPrimary,
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            {t('layout.dialog.changePasswordTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: GS_COLORS.textMuted, lineHeight: 1.5, px: 1 }}
          >
            {t('layout.dialog.changePasswordSubtitle')}
          </Typography>
        </Box>

        {/* ── Fields ── */}
        <DialogContent sx={{ pt: 3, pb: 1, px: 3, bgcolor: '#fff', overflow: 'visible' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label={t('layout.dialog.currentPassword')}
              type={showCurrentPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={savingPassword}
              sx={profileDialogFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle current password visibility"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      edge="end"
                      size="small"
                      sx={{ color: GS_COLORS.textMuted }}
                    >
                      {getPasswordVisibilityIcon(showCurrentPassword)}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('layout.dialog.newPassword')}
              type={showNewPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={savingPassword}
              sx={profileDialogFieldSx}
              error={(attemptedPasswordSave || newPassword.length > 0) && validatePassword(newPassword) !== null}
              helperText={getNewPasswordHelperText(attemptedPasswordSave, newPassword, t)}
              FormHelperTextProps={{ sx: { color: '#DC2626', ml: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword((v) => !v)}
                      edge="end"
                      size="small"
                      sx={{ color: GS_COLORS.textMuted }}
                    >
                      {getPasswordVisibilityIcon(showNewPassword)}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('layout.dialog.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={savingPassword}
              sx={getConfirmPasswordFieldSx(confirmPassword, newPassword)}
              helperText={getConfirmPasswordHelperText(confirmPassword, newPassword, t)}
              FormHelperTextProps={{ sx: { color: '#DC2626', ml: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                      size="small"
                      sx={{ color: GS_COLORS.textMuted }}
                    >
                      {getPasswordVisibilityIcon(showConfirmPassword)}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        {/* ── Actions ── */}
        <Box sx={{ px: 3, pt: 2, pb: 3, bgcolor: '#fff' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSavePassword}
            disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              borderRadius: '12px',
              py: 1.4,
              bgcolor: GS_COLORS.orange,
              boxShadow: '0 6px 16px rgba(255, 140, 66, 0.38)',
              '&:hover': { bgcolor: '#E06526', boxShadow: '0 8px 20px rgba(255, 140, 66, 0.45)' },
              '&.Mui-disabled': { bgcolor: 'rgba(232,113,26,0.38)', color: '#fff' },
            }}
          >
            {savingPassword ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('layout.dialog.updatePassword')}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => setPasswordDialogOpen(false)}
            disabled={savingPassword}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              mt: 1,
              color: GS_COLORS.textMuted,
              '&:hover': { bgcolor: 'rgba(30, 58, 69, 0.05)', color: GS_COLORS.textPrimary },
            }}
          >
            {t('layout.dialog.cancel')}
          </Button>
        </Box>
      </Dialog>

      {/* ── Help Center Dialog ── */}
      <Dialog
        open={helpCenterOpen}
        onClose={() => !submittingHelp && setHelpCenterOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px -12px rgba(15, 23, 42, 0.18)',
            border: `1px solid ${GS_COLORS.notifBorder}`,
          },
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, #FFF0E6 0%, #FFE4CC 100%)`,
            pt: 4,
            pb: 3.5,
            px: 3,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setHelpCenterOpen(false)}
            disabled={submittingHelp}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: GS_COLORS.textMuted,
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 300 }}>✕</span>
          </IconButton>

          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: GS_COLORS.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              boxShadow: '0 8px 24px rgba(255, 140, 66, 0.35)',
            }}
          >
            <HelpOutlineIcon sx={{ color: '#fff', fontSize: 36 }} />
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: GS_COLORS.textPrimary, mb: 0.5 }}>
            {t('layout.dialog.helpCenterTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: GS_COLORS.textMuted, lineHeight: 1.5, px: 1 }}>
            {t('layout.dialog.helpCenterSubtitle')}
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1, px: 3, bgcolor: '#fff' }}>


          {/* ── Contact Support ── */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {/* Primary Phone */}
            <Box
              component="a"
              href="tel:+49 (0176) 58691340"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: GS_COLORS.textPrimary,
                transition: 'all 0.2s ease',
                '&:hover': { color: GS_COLORS.orange }
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F0F7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HeadsetMicIcon sx={{ fontSize: 18, color: GS_COLORS.orange }} />
              </Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>+49 (0176) 58691340</Typography>
            </Box>


            {/* Email - Open Gmail */}
            <Box
              component="a"
              href="https://mail.google.com/mail/?view=cm&fs=1&to=info@haari-ug.de"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: GS_COLORS.textPrimary,
                transition: 'all 0.2s ease',
                '&:hover': { color: GS_COLORS.orange }
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MailOutlineIcon sx={{ fontSize: 18, color: '#0284C7' }} />
              </Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}> info@welivin.app</Typography>
            </Box>
          </Stack>

          <TextField
            autoFocus
            label={t('layout.dialog.helpCenterMessage')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={helpMessage}
            onChange={(e) => setHelpMessage(e.target.value)}
            disabled={submittingHelp}
            sx={profileDialogDisplayNameFieldSx}
          />
        </DialogContent>

        <Box sx={{ px: 3, pt: 2, pb: 3, bgcolor: '#fff' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveHelp}
            disabled={submittingHelp || !helpMessage.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '12px',
              py: 1.4,
              bgcolor: GS_COLORS.orange,
              boxShadow: '0 6px 16px rgba(255, 140, 66, 0.38)',
              '&:hover': { bgcolor: '#E06526' },
            }}
          >
            {submittingHelp ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('layout.dialog.save')}
          </Button>
        </Box>
      </Dialog>


      <LayoutSidebar
        isDesktop={isDesktop}
        open={open}
        setOpen={setOpen}
        navItems={navItems}
        pathname={location.pathname}
        navigate={navigate}
        logout={logout}
        setHelpCenterOpen={setHelpCenterOpen}
        translate={t}
      />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 0, width: 'auto', mt: { xs: 8, sm: 10 }, minHeight: '100vh', overflowX: 'hidden' }}>
        <Outlet context={{ setHelpCenterOpen }} />
      </Box>
    </Box>
  );
};

export default Layout;
